import { Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MenuItem, ToastMessageOptions } from "primeng/api";
import {
  EntityModelMzTabResult,
  EntityModelSubmission,
  FileResource,
  MzTabResult,
  SubmissionControllerService
} from "../../../../modules/lipidcompass-backend-client";
import { JobsService } from "../../../../modules/lipidcompass-data-importer-client";
import { submissionWorkflowConfig } from "../../../environments/environment";
import { MzTab } from "../../../../modules/mztabm-validator-client";
import { forkJoin, Observable } from "rxjs";
import { LoggingService, LogLevel } from "../../_services/logging.service";
import { datasetsOf } from "../../_services/submission-datasets.util";
import { CommonModule } from "@angular/common";
import { CardModule } from "primeng/card";
import { MessageModule } from "primeng/message";
import { DataViewModule } from "primeng/dataview";
import { TableModule } from "primeng/table";
import { DatasetCardComponent } from "../../dataset/dataset-card/dataset-card.component";
import { ButtonModule } from "primeng/button";
import { TranslateModule } from "@ngx-translate/core";

@Component({
  selector: "app-submission",
  templateUrl: "./submission.component.html",
  styleUrls: ["./submission.component.css"],
  standalone: true,
  imports: [
    ButtonModule,
    CardModule,
    CommonModule,
    DatasetCardComponent,
    DataViewModule,
    MessageModule,
    TableModule,
    TranslateModule
  ]
})
export class SubmissionComponent implements OnInit {
  private submissionService = inject(SubmissionControllerService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private loggingService = inject(LoggingService);

  submissionId: string | undefined;
  activeIndex: number = 4;
  submission: EntityModelSubmission | undefined;
  mzTabMResults: Observable<MzTabResult[]> | undefined;
  datasetCardShowClassifications: boolean = true;
  datasetCardDetailView: boolean = true;
  datasetCardShowDatasetLink: boolean = false;
  datasetCardShowPlots: boolean = false;
  datasetCardToggleable: boolean = false;

  validationWarnMessage: ToastMessageOptions = {
    icon: "pi pi-exclamation-triangle",
    closable: false,
    summary: "Validation Issues",
    detail:
      "Please review your submission carefully. You have not yet uploaded all required files or some files may not have passed validation! You can only proceed to submit your study, if all errors or warnings have been resolved. Please contact us in case of questions.",
    key: "validationMessage",
    severity: "warn"
  };

  validationSuccessMessage: ToastMessageOptions = {
    icon: "pi pi-check",
    closable: false,
    summary: "Validation Successful",
    detail:
      "Thank you for submitting all required files. Your data files have passed validation! You can now proceed to submit your study for curation. Please contact us in case of questions.",
    key: "validationSuccessMessage",
    severity: "success"
  };

  ngOnInit() {
    this.submissionId = this.route.snapshot.params.id;
    if (this.submissionId) {
      this.submissionService.submissionGetById(this.submissionId).subscribe(result => {
        this.submission = <EntityModelSubmission>result;
        // One preview per DATASET. Filtering on fileType alone requested a
        // preview for the ORIGINAL and for the WORKING copy derived from it,
        // rendering the same dataset card twice.
        var mzTabMfiles: FileResource[] = datasetsOf(
          this.submission?.submittedFiles
        );
        this.loggingService.log(
          "SubmissionComponent",
          "Found the following mzTab datasets: " + JSON.stringify(mzTabMfiles),
          LogLevel.DEBUG
        );
        // loop over mzTabMfiles and create MzTabResult for each
        this.mzTabMResults = this.getMzTabResults(
          this.submissionId,
          mzTabMfiles
        );
      });
    }
  }

  public getMzTabResults(
    submissionId: string | undefined,
    fileResources: FileResource[]
  ): Observable<EntityModelMzTabResult[]> {
    if (!submissionId) {
      this.loggingService.log(
        "SubmissionComponent",
        "No submission ID found",
        LogLevel.DEBUG
      );
      return new Observable<EntityModelMzTabResult[]>();
    }
    var submissionService = this.submissionService;
    this.loggingService.log(
      "SubmissionComponent",
      "Retrieving the following mzTab files: " + JSON.stringify(fileResources),
      LogLevel.DEBUG
    );
    return forkJoin(
      fileResources.map(function(
        value,
        index
      ): Observable<EntityModelMzTabResult> {
        return submissionService.submissionGetMzTabResult(submissionId, value);
      })
    );
  }

  createFileType(e): string {
    let fileType: string = "";
    if (e == "pdf" || e == "csv") {
      fileType = `application/${e}`;
    } else if (e == "jpeg" || e == "jpg" || e == "png") {
      fileType = `image/${e}`;
    } else if (e == "txt") {
      fileType = "text/plain";
    } else if (e == "mztab") {
      fileType = "text/plain";
    } else if (e == "ppt" || e == "pot" || e == "pps" || e == "ppa") {
      fileType = "application/vnd.ms-powerpoint";
    } else if (e == "pptx") {
      fileType =
        "application/vnd.openxmlformats-officedocument.presentationml.presentation";
    } else if (e == "doc" || e == "dot") {
      fileType = "application/msword";
    } else if (e == "docx") {
      fileType =
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    } else if (e == "xls" || e == "xlt" || e == "xla") {
      fileType = "application/vnd.ms-excel";
    } else if (e == "xlsx") {
      fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    }

    return fileType;
  }

  downloadFile(submissionId: string, fileResource: FileResource) {
    this.submissionService
      .submissionDownloadFile(submissionId, fileResource, "response")
      .subscribe(response => {
        this.loggingService.log(
          "DataSubmitComponent",
          "Received response: " + JSON.stringify(response),
          LogLevel.DEBUG
        );
        if (!response) {
          this.loggingService.log(
            "DataSubmitComponent",
            "No response received!",
            LogLevel.ERROR
          );
          return;
        }
        let header_content = response.headers.get("Content-Disposition");
        let file = header_content.split("=")[1];
        file = file.substring(1, file.length - 1);
        let extension = file.split(".")[1].toLowerCase();

        // set the mime type explicitly
        var newBlob = new Blob([response.body], {
          type: this.createFileType(extension)
        });
        //const data = window.URL.createObjectURL(newBlob);
        var downloadURL = window.URL.createObjectURL(newBlob);
        var link = document.createElement("a");
        link.href = downloadURL;
        link.download = fileResource.fileName;
        link.click();
        // setTimeout(() => {
        //   window.URL.revokeObjectURL(data);
        // }, 400)
      });
  }

  isSubmissionComplete(): boolean {
    // check that this.submission is not null
    var isComplete: boolean = false;
    if (this.submission) {
      // isComplete = this.submission.status === "IN_PROGRESS";
      this.loggingService.log(
        "DataSubmitComponent",
        "isSubmissionComplete: submission exists",
        LogLevel.DEBUG
      );
      // count the number of mzTab-M DATASETS (not artifacts: the ORIGINAL and
      // its derived WORKING copy are one dataset, and counting both would
      // double every error and warning below)
      var mzTabMfiles: FileResource[] = datasetsOf(
        this.submission?.submittedFiles
      );
      var nMzTabMfiles: number = mzTabMfiles.length;
      this.loggingService.log(
        "DataSubmitComponent",
        "isSubmissionComplete: nMzTabMfile: " + nMzTabMfiles,
        LogLevel.DEBUG
      );
      // check that the MZTAB_M files have been validated by checking that the lastValidated field is not null
      var mzTabMfilesValidated: boolean = false;
      if (nMzTabMfiles && nMzTabMfiles > 0) {
        if (mzTabMfiles) {
          mzTabMfilesValidated = mzTabMfiles.every(
            value => value.lastValidated !== null
          );
          this.loggingService.log(
            "DataSubmitComponent",
            "isSubmissionComplete: mzTabMfilesValidated: " +
              mzTabMfilesValidated,
            LogLevel.DEBUG
          );
          // for each MZTAB_M file, check the validationMessages. Count messages where level === "ERROR"
          var nErrors = 0;
          mzTabMfiles.forEach(value => {
            nErrors +=
              (value?.validationMessages || []).filter(
                value => value.level === "ERROR"
              ).length || 0;
          });
          // for each MZTAB_M file, check the validationMessages. Count messages where level === "WARN"
          var nWarnings = 0;
          mzTabMfiles.forEach(value => {
            nWarnings +=
              (value?.validationMessages || []).filter(
                value => value.level === "WARN"
              ).length || 0;
          });
          this.loggingService.log(
            "DataSubmitComponent",
            "isSubmissionComplete: nErrors: " +
              nErrors +
              " nWarnings: " +
              nWarnings,
            LogLevel.DEBUG
          );
          // if we are in strictValidation mode, then we need to check that nErrors === 0
          isComplete = mzTabMfilesValidated && nErrors === 0;
          this.loggingService.log(
            "DataSubmitComponent",
            "isSubmissionComplete: isComplete: " + isComplete,
            LogLevel.DEBUG
          );
          if (submissionWorkflowConfig.strictValidation) {
            isComplete = isComplete && nWarnings === 0;
            this.loggingService.log(
              "DataSubmitComponent",
              "isSubmissionComplete: isComplete strict: " + isComplete,
              LogLevel.DEBUG
            );
          }
        }
      }
    }
    return isComplete;
  }

  nextPage(event: any) {
    if (this.submission) {
      this.submission.status = "SUBMITTED";
      this.submissionService
        .submissionSaveSingle(this.submission)
        .subscribe(response => {
          this.loggingService.log(
            "SubmissionComponent",
            "NextPage saved submission: " + JSON.stringify(response),
            LogLevel.DEBUG
          );
          var submissionMessage: ToastMessageOptions = {
            closable: true,
            severity: "info",
            summary: "Submission completed",
            detail:
              "Your study submission to LipidCompass has been recorded successfully! Thank you for your submission!"
          };
          this.router.navigate(submissionWorkflowConfig.parentRoute);
        });
    } else {
      this.router.navigate(submissionWorkflowConfig.parentRoute);
    }
    return;
  }

  previousPage(event: any) {
    this.loggingService.log(
      "SubmissionComponent",
      "previousPage: " + JSON.stringify(event),
      LogLevel.DEBUG
    );
    if (this.activeIndex > 1) {
      this.loggingService.log(
        "SubmissionComponent",
        "previousPage via activeIndex: " +
          JSON.stringify([
            submissionWorkflowConfig.workflows.mzTab.steps[this.activeIndex - 2]
              .routerLink,
            this.submissionId
          ]),
        LogLevel.DEBUG
      );
      this.loggingService.log(
        "SubmissionComponent",
        "Active index: " +
          (this.activeIndex - 2) +
          " is " +
          JSON.stringify(
            submissionWorkflowConfig.workflows.mzTab.steps[this.activeIndex - 2]
              .routerLink
          ),
        LogLevel.DEBUG
      );
      this.router.navigate(
        [
          submissionWorkflowConfig.workflows.mzTab.steps[this.activeIndex - 2]
            .routerLink,
          this.submissionId
        ],
        { relativeTo: this.route.parent }
      );
    } else {
      this.loggingService.log(
        "SubmissionComponent",
        "previousPage to parent: " + JSON.stringify([this.route.parent]),
        LogLevel.DEBUG
      );
      this.router.navigate(submissionWorkflowConfig.parentRoute);
    }
    return;
  }

  cancel(event: any) {
    this.router.navigate(submissionWorkflowConfig.parentRoute);
    return;
  }
}
